import React, { useEffect, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import portalApiClient from "../../api/portalAxios";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { usePublicBranding } from "../../hooks/usePublicBranding";
import { getPortalSubdomain } from "../../utils/portalSubdomain";

interface ChatApiEnvelope {
  success: boolean;
  data?: {
    clientId?: string | null;
    message: string;
    response: string;
  };
  message?: string | null;
  errors?: string[] | null;
}

function formatChatRequestError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const raw = error.response?.data;
    let bodyMessage = "";
    if (raw && typeof raw === "object") {
      const o = raw as Record<string, unknown>;
      if (typeof o.message === "string") {
        bodyMessage = o.message;
      } else if (Array.isArray(o.errors) && o.errors.length) {
        bodyMessage = o.errors.filter((e) => typeof e === "string").join("; ");
      } else if (typeof o.title === "string") {
        bodyMessage = o.title;
      }
    }
    const parts = [
      status ? `HTTP ${status}` : null,
      bodyMessage || null,
      error.message || null,
    ].filter(Boolean);
    return parts.length ? parts.join(" — ") : "Request failed.";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong.";
}

interface Message {
  role: "user" | "assistant";
  content: string;
  sentAt: number;
}

const markdownClass =
  "text-sm text-slate-800 [&_p]:mb-2 [&_p]:last:mb-0 [&_ul]:mb-2 [&_ul]:ml-4 [&_ul]:list-disc [&_strong]:font-semibold [&_a]:text-navy-600 [&_a]:underline";

function formatTime(ts: number) {
  return new Date(ts).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const PortalChatWidget: React.FC = () => {
  const { user, me } = usePortalAuth();
  const subdomain = me?.subdomain || user?.subdomain || getPortalSubdomain();
  const { branding } = usePublicBranding(subdomain);

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);

  const botName =
    branding?.chatbotName || me?.chatbotName || user?.chatbotName || "Assistant";
  const welcome =
    branding?.chatbotWelcomeMessage ||
    me?.chatbotWelcomeMessage ||
    user?.chatbotWelcomeMessage ||
    `Hi! I’m ${botName}. Ask me general questions about your repair journey — I can explain outcomes and next steps in everyday language.`;
  const clientId = (me?.clientId ?? user?.clientId ?? "").trim();

  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: welcome, sentAt: Date.now() },
  ]);

  useEffect(() => {
    setMessages([{ role: "assistant", content: welcome, sentAt: Date.now() }]);
  }, [welcome]);

  const send = async () => {
    const text = input.trim();
    if (!text) {
      return;
    }
    if (!clientId) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "We couldn’t identify your client profile. Please refresh the page or sign in again, then try the chat.",
          sentAt: Date.now(),
        },
      ]);
      return;
    }

    const historyForApi = messages.map((m) => ({ role: m.role, content: m.content }));
    const ts = Date.now();
    setMessages((prev) => [...prev, { role: "user", content: text, sentAt: ts }]);
    setInput("");

    setTyping(true);
    try {
      const { data: envelope } = await portalApiClient.post<ChatApiEnvelope>("/ai/chat", {
        clientId,
        message: text,
        history: historyForApi,
      });

      if (!envelope.success || envelope.data === undefined) {
        const detail = [envelope.message, envelope.errors?.join("; ")].filter(Boolean).join(" — ");
        throw new Error(detail || "Chat request was not successful.");
      }

      const reply = envelope.data.response?.trim();
      if (!reply) {
        throw new Error("The server returned an empty response.");
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: reply,
          sentAt: Date.now(),
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `**Couldn’t reach the assistant.**\n\n${formatChatRequestError(err)}\n\nIf this keeps happening, contact your advisor.`,
          sentAt: Date.now(),
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  const businessName = branding?.businessName || user?.operatorBusinessName || "Your advisor";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-[max(1.5rem,env(safe-area-inset-right))] z-50 flex h-14 w-14 items-center justify-center rounded-full bg-navy-600 text-white shadow-lg transition-colors hover:bg-navy-800"
        aria-label="Open messages"
      >
        <span className="text-lg font-semibold">?</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col overflow-hidden border-0 bg-white shadow-2xl lg:inset-auto lg:bottom-24 lg:right-6 lg:h-[min(460px,calc(100vh-7rem))] lg:w-[min(340px,calc(100vw-1.5rem))] lg:rounded-2xl lg:border lg:border-slate-200">
          <div className="flex items-start justify-between gap-2 border-b border-navy-800 bg-navy-900 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] lg:pt-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-navy-100">AI Assistant</p>
              <p className="truncate text-sm font-semibold text-white">{businessName}</p>
              <h3 className="mt-0.5 text-xs font-medium text-navy-100">{botName}</h3>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="shrink-0 rounded-md border border-white/20 p-2 text-white hover:bg-white/10 lg:hidden"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="min-h-0 min-w-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden bg-slate-50 px-3 py-3">
            {messages.map((m, idx) => (
              <div
                key={`${m.role}-${idx}-${m.sentAt}`}
                className={`flex flex-col gap-0.5 ${m.role === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[88%] rounded-2xl bg-navy-600 px-3 py-2 text-white"
                      : "max-w-[88%] rounded-2xl border border-slate-200 bg-slate-100 px-3 py-2 text-slate-800 shadow-sm"
                  }
                >
                  {m.role === "assistant" ? (
                    <div className={markdownClass}>
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm">{m.content}</p>
                  )}
                </div>
                <span className="px-1 text-[10px] text-slate-400">{formatTime(m.sentAt)}</span>
              </div>
            ))}
            {typing && (
              <div className="flex gap-1 px-2 py-2">
                <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-500 [animation-duration:0.55s]" />
                <span
                  className="h-2 w-2 animate-bounce rounded-full bg-emerald-500 [animation-duration:0.55s]"
                  style={{ animationDelay: "120ms" }}
                />
                <span
                  className="h-2 w-2 animate-bounce rounded-full bg-emerald-500 [animation-duration:0.55s]"
                  style={{ animationDelay: "240ms" }}
                />
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    void send();
                  }
                }}
                placeholder="Ask a question…"
                className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-navy-600 focus:ring-2 focus:ring-navy-600/30"
              />
              <button
                type="button"
                onClick={() => void send()}
                className="w-full shrink-0 rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-600 sm:w-auto"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PortalChatWidget;
